import { c as _c } from "react/compiler-runtime";
/**
 * Coverage-related data fetchers
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createOrUpdateCoverage } from "@/src/actions/coverage";
import { toast } from "@/src/hooks/use-toast";
import { PATIENTS_CACHE_KEY } from "@/src/lib/data-fetchers/patients-fetchers";
import { REFERRALS_CACHE_KEY } from "@/src/lib/data-fetchers/referrals-fetchers";
import { TASK_CACHE_KEY, TASKS_CACHE_KEY } from "@/src/lib/data-fetchers/tasks-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { Coverage } from "@/src/models/coverage";
import { Patient } from "@/src/models/patient";
function patchPatientCoverages(patient: Patient, savedCoverage: Coverage): Patient {
  if (patient.id !== savedCoverage.patientId) {
    return patient;
  }
  const coverages = patient.coverages ?? [];
  const exists = coverages.some(coverage => coverage.id === savedCoverage.id);
  return {
    ...patient,
    coverages: exists ? coverages.map(coverage => coverage.id === savedCoverage.id ? {
      ...coverage,
      ...savedCoverage
    } : coverage) : [...coverages, savedCoverage]
  };
}
export function useCoverageUpdateMutation() {
  const $ = _c(5);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = savedCoverage => {
      queryClient.setQueriesData({
        queryKey: [PATIENTS_CACHE_KEY]
      }, data => {
        if (Array.isArray(data)) {
          return (data as Patient[]).map(patient => patchPatientCoverages(patient, savedCoverage));
        }
        if (data && typeof data === "object" && "id" in data) {
          return patchPatientCoverages(data as Patient, savedCoverage);
        }
        return data;
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      invalidates: [[PATIENTS_CACHE_KEY], [REFERRALS_CACHE_KEY], [TASKS_CACHE_KEY], [TASK_CACHE_KEY]]
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t0) {
    t2 = {
      mutationFn: _temp,
      onSuccess: t0,
      onError: _temp2,
      meta: t1
    };
    $[3] = t0;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useMutation(t2);
}

/**
 * Fetch the top insurance payers
 */
function _temp2(error) {
  logger.error("Error updating coverage:", {
    error
  });
  toast({
    title: "Error updating coverage",
    description: "There was an error saving the coverage information. Please try again.",
    variant: "destructive"
  });
}
async function _temp(coverage) {
  return await createOrUpdateCoverage(coverage, true);
}
export function useInsuranceTopPayers() {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["insurance-top-payers"],
      queryFn: _temp3,
      staleTime: Infinity
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data,
    isLoading,
    isError
  } = useQuery(t0);
  let t1;
  if ($[1] !== data) {
    t1 = data || [];
    $[1] = data;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== isError || $[4] !== isLoading || $[5] !== t1) {
    t2 = {
      topInsurancePayers: t1,
      isLoading,
      isError
    };
    $[3] = isError;
    $[4] = isLoading;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
async function _temp3() {
  const res = await fetch("/api/insurance-top-payers");
  if (!res.ok) {
    throw new Error("Failed to fetch insurance top payers");
  }
  return res.json();
}